import {
  Users,
  Shield,
  MessageSquareDot,
  MessageSquareHeart,
  MessagesSquare,
  HeartPulse,
  Activity,
  Radio,
  Scale,
  Link2,
  Languages,
  Settings,
  Inbox,
} from 'lucide-react';
import type { NavSection } from './sidebar-primitives';
import { NAV_KEY, navGroup, navItem } from '@/i18n/keys/nav';

/**
 * The admin nav, as data (design grammar §4.3).
 *
 * Extracted from `Sidebar.tsx` alongside `parent-nav.ts`, on the shape
 * `trfer-nav.ts` established. Same reason as the parent extraction: while the
 * list lived inside the component, `HelpPage` carried a hand-written second
 * copy that documented five of eighteen surfaces, and nothing could catch the
 * gap. `HelpPage.navParity.test.tsx` now fails the build in both directions.
 *
 * GROUPS — rewritten 2026-09-20 (admin-section subtraction, Victor: "remove
 * any features that are already accessible through the TRFer role — we don't
 * need to have both").
 *
 * This SUPERSEDES the 2026-08-26 ruling that the admin sidebar mirrors the
 * TRFer groups. That ruling was sound given its constraint — MainLayout picks
 * the sidebar shell from `user.role`, not from the route, so an admin who
 * followed a link to /trfer/applications got the ADMIN shell around a TRFer
 * page, and mirroring the group names was the only way to keep one label per
 * destination. The constraint is gone: admins now hold the `trfer` role, so
 * the sidebar's own role switcher opens the real TRFer desk, and a mirrored
 * row would be a second address for a page the reader can already reach.
 *
 *   System      System health · Platform stats · Translation reports
 *   Oversee     Conversation review · Handoff oversight · User feedback
 *   Configure   Access control · Platform settings · Handoff config · Publish policy · Payment accounts
 *
 * WHAT LEFT, and where it went:
 *   Pending signups    -> /trfer/users?status=pending (same list, same actions)
 *   Applications       -> /trfer/applications (the row already pointed there)
 *   Requests           -> /trfer/grants       (ditto)
 *   Forms              -> /trfer/forms        (same component, scope prop only)
 *   Polls              -> /trfer/polls        (ditto)
 *   Partner hospitals  -> /trfer/hospitals    (moved, Task 2)
 *   Agent status       -> /admin/health       (absorbed; live gRPC, not cached stats)
 *   Spec 12 checks     -> backend/tests/services/spec12-checks.test.js
 *                         (2026-10-19: the legal-consent self-test runs in CI)
 *
 * "Access control" is the old "User management". The new name says what only
 * this page can do — create an account, assign the admin role, toggle Lead
 * TRFer, hard-delete, read the audit log. The old name read like the TRFer
 * roster it partly duplicated.
 *
 * "Handoff config" RETURNED 2026-09-21 (Victor's ruling, DECISIONS 2026-09-20
 * amendment): staffing mode, office hours, SLA windows and per-signal routing
 * are org-wide operational configuration, the same class as the feature flags
 * this branch promoted into Platform settings above — real admin data by
 * Victor's own "only real admin data" test, not a TRFer-desk duplicate. It
 * sits right after Platform settings: both are the platform's own knobs,
 * distinct from the identity/publish/money rows that follow. The TRFer
 * Settings Handoff tab is unaffected — `AdminHandoffConfigPage` mounts
 * `embedded` there and full-page here, one component in two shells, which is
 * a deliberate exception to "no duplicates" (the amendment spells out why).
 *
 * Admin dashboard sits UNGROUPED at the top, like Today. Its old "Overview"
 * label was a heading over a single item, which is a heading, not a group.
 */

export interface AdminNavPermissions {
  /**
   * `user.permissions.can_review_applications`.
   *
   * INERT since 2026-09-20: every remaining row is admin-only and ungated,
   * because the permission-gated surfaces all moved to the TRFer desk in the
   * same subtraction — Applications, and Pre-screen appeals until the appeals
   * process ended on 2026-10-26 (#3307). The parameter stays because `parentFor.ts` and `HelpPage.navParity.test.tsx` both call this
   * builder with it, and because a permission-gated admin row is a thing that
   * could return. Gating is PER ITEM, never per group (design grammar §4.3,
   * DECISIONS 2026-08-21).
   */
  canReviewApplications: boolean;
}

/**
 * Build the admin nav for one reader.
 *
 * `_permissions` is accepted and ignored — see `AdminNavPermissions` above.
 */
export function buildAdminNavSections(
  _permissions: AdminNavPermissions,
): NavSection[] {
  return [
    {
      // `end` is DATA, declared on the item (grammar §4.3) — /admin is a
      // prefix of every other admin route, so without it the dashboard row
      // stays lit on every child page.
      items: [
        navItem(NAV_KEY.adminDashboard, '/admin', Shield, { end: true }),
      ],
    },
    // What the platform is doing, as a machine. System health is first
    // because it is the one page that answers "is anything on fire".
    navGroup(NAV_KEY.groupSystem, [
        navItem(NAV_KEY.systemHealth,       '/admin/health',              HeartPulse),
        navItem(NAV_KEY.platformStats,      '/admin/stats',               Activity),
        navItem(NAV_KEY.translationReports, '/admin/translation-reports', Languages),
    ]),
    // Read-mostly windows onto work happening elsewhere. None of these is
    // a queue the admin works; each is a view they watch.
    navGroup(NAV_KEY.groupOversee, [
        navItem(NAV_KEY.conversationReview, '/admin/reviews',  MessageSquareDot),
        // The transcript reader (Victor, 2026-10-23): a family's conversation
        // permission opens their companion conversations to admins here, not to
        // TRFers or William. Every open is audited; nothing is rated.
        navItem(NAV_KEY.adminConversations, '/admin/conversations', MessagesSquare),
        navItem(NAV_KEY.handoffOversight,   '/admin/handoffs', Radio),
        navItem(NAV_KEY.userFeedback,       '/admin/feedback', MessageSquareHeart),
    ]),
    // The knobs. Access control leads: who may act is the first thing
    // configured, and it is the page that grants a new admin their roles.
    navGroup(NAV_KEY.groupConfigure, [
        navItem(NAV_KEY.accessControl,    '/admin/users',           Users),
        navItem(NAV_KEY.platformSettings, '/admin/settings',        Settings),
        navItem(NAV_KEY.handoffConfig,    '/admin/handoff-config',  Inbox),
        navItem(NAV_KEY.publishPolicy,    '/admin/legal',           Scale),
        navItem(NAV_KEY.paymentAccounts,  '/admin/bill-links',      Link2),
    ]),
  ];
}
